import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import {
    useGetDealerStockDealerDropdownQuery,
    useLazyGetDealerStockWarehouseDropdownQuery,
    useLazyGetDealerStockPartDropdownQuery,
    useAddDealerStockMutation
} from '../../../../api/endpoints/dealerStockAdd';
import { toastService } from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';

const AddStockDialog = ({ visible, onHide, onSuccess }) => {
    const { th } = useLanguage();

    const [selectedDealer, setSelectedDealer] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedPart, setSelectedPart] = useState(null);
    const [quantity, setQuantity] = useState(0);

    const [dealerSearchText, setDealerSearchText] = useState('');
    const [dealerDropdownKey, setDealerDropdownKey] = useState(0);
    const [partDropdownKey, setPartDropdownKey] = useState(0);

    const dealerFilterTimeoutRef = useRef(null);
    const partFilterTimeoutRef = useRef(null);

    const { data: dealerList = [], isLoading: isLoadingDealers } = useGetDealerStockDealerDropdownQuery({
        SearchText: dealerSearchText || ' ',
        PageNumber: 1,
        PageSize: 50
    });

    const [triggerWarehouseDropdown, { data: warehouseList = [], isFetching: isLoadingWarehouses }] = useLazyGetDealerStockWarehouseDropdownQuery();
    const [triggerPartDropdown, { data: partList = [], isFetching: isLoadingParts }] = useLazyGetDealerStockPartDropdownQuery();

    const [addDealerStock, { isLoading: isSubmitting }] = useAddDealerStockMutation();

    const dealerOptions = useMemo(() => {
        const options = (dealerList || []).map((d) => ({
            id: d.id ?? d.dealerId,
            name: d.name ?? d.dealerName ?? `${d.id ?? d.dealerId}`,
            displayValue: d.name ?? d.dealerName ?? `${d.id ?? d.dealerId}`
        }));
        if (selectedDealer && !options.find((o) => o.id === selectedDealer.id)) {
            options.unshift(selectedDealer);
        }
        return options;
    }, [dealerList, selectedDealer]);

    const warehouseOptions = useMemo(() => {
        return (warehouseList || []).map((w, index) => {
            const warehouseId = w.id ?? w.warehouseId ?? w.wareHouseId ?? w.value ?? w.key ?? index;
            return {
                id: warehouseId,
                name: w.name ?? w.warehouseName ?? w.text ?? w.label ?? `${warehouseId}`,
                displayValue: w.name ?? w.warehouseName ?? w.text ?? w.label ?? `${warehouseId}`,
                raw: w
            };
        });
    }, [warehouseList]);

    const partOptions = useMemo(() => {
        const options = (partList || []).map((p) => ({
            code: p.id ?? p.partCode ?? p.code,
            name: p.name ?? p.partName ?? '',
            stockQuantity: p.quantity ?? 0,
            displayValue: `${p.id ?? p.partCode ?? p.code} - ${p.name ?? p.partName ?? ''}`
        }));
        if (selectedPart && !options.find((o) => o.code === selectedPart.code)) {
            options.unshift(selectedPart);
        }
        return options;
    }, [partList, selectedPart]);

    const fetchParts = useCallback((warehouseId, dealerId, searchText) => {
        if (dealerId != null && warehouseId != null) {
            triggerPartDropdown({
                DealerId: dealerId,
                WareHouseId: warehouseId,
                SearchText: searchText || ' ',
                PageNumber: 1,
                PageSize: 50
            });
        }
    }, [triggerPartDropdown]);

    useEffect(() => {
        if (selectedDealer?.id) {
            triggerWarehouseDropdown({ DealerId: selectedDealer.id });
            setSelectedWarehouse(null);
            setSelectedPart(null);
            setPartDropdownKey((prev) => prev + 1);
        }
    }, [selectedDealer, triggerWarehouseDropdown]);

    useEffect(() => {
        if (selectedWarehouse && selectedDealer?.id) {
            const whId = selectedWarehouse.id ?? selectedWarehouse.raw?.id ?? selectedWarehouse.raw?.warehouseId ?? selectedWarehouse.raw?.wareHouseId;
            
            setSelectedPart(null);
            setPartDropdownKey((prev) => prev + 1);
            if (whId != null) {
                fetchParts(whId, selectedDealer.id, '');
            }
        }
    }, [selectedWarehouse, selectedDealer, fetchParts]);

    useEffect(() => {
        if (!visible) {
            setSelectedDealer(null);
            setSelectedWarehouse(null);
            setSelectedPart(null);
            setQuantity(0);
            setDealerSearchText('');
            setDealerDropdownKey((prev) => prev + 1);
            setPartDropdownKey((prev) => prev + 1);
        }
    }, [visible]);

    const handleDealerFilter = useCallback((e) => {
        if (dealerFilterTimeoutRef.current) {
            clearTimeout(dealerFilterTimeoutRef.current);
        }
        dealerFilterTimeoutRef.current = setTimeout(() => {
            setDealerSearchText(e.filter);
        }, 400);
    }, []);

    const handleDealerHide = useCallback(() => {
        setDealerSearchText('');
        setDealerDropdownKey((prev) => prev + 1);
    }, []);

    const handlePartFilter = useCallback((e) => {
        if (partFilterTimeoutRef.current) {
            clearTimeout(partFilterTimeoutRef.current);
        }
        partFilterTimeoutRef.current = setTimeout(() => {
            const whId = selectedWarehouse?.id ?? selectedWarehouse?.raw?.id ?? selectedWarehouse?.raw?.warehouseId ?? selectedWarehouse?.raw?.wareHouseId;
            if (selectedDealer?.id && whId != null) {
                fetchParts(whId, selectedDealer.id, e.filter);
            }
        }, 400);
    }, [selectedDealer, selectedWarehouse, fetchParts]);

    const handlePartHide = useCallback(() => {
        const whId = selectedWarehouse?.id ?? selectedWarehouse?.raw?.id ?? selectedWarehouse?.raw?.warehouseId ?? selectedWarehouse?.raw?.wareHouseId;
        if (selectedDealer?.id && whId != null) {
            fetchParts(whId, selectedDealer.id, '');
        }
        setPartDropdownKey((prev) => prev + 1);
    }, [selectedDealer, selectedWarehouse, fetchParts]);

    const handleSubmit = async () => {
        if (!selectedDealer || !selectedWarehouse || !selectedPart || !quantity || quantity <= 0) {
            toastService.warn(th('dealerStockAdd.validation.fillAllFields'));
            return;
        }

        try {
            await addDealerStock({
                partCode: selectedPart.code,
                wareHouseId: selectedWarehouse.id,
                dealerId: selectedDealer.id,
                quantity: quantity
            }).unwrap();

            toastService.success(th('dealerStockAdd.messages.addSuccess'));
            onHide();
            onSuccess?.();
        } catch (error) {
            console.error('Stok ekleme hatası:', error);
            toastService.error(error?.data?.message || th('dealerStockAdd.messages.addError'));
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={th('common.cancel')}
                icon="pi pi-times"
                outlined
                onClick={onHide}
                disabled={isSubmitting}
            />
            <Button
                label={th('common.save')}
                icon="pi pi-check"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={!selectedDealer || !selectedWarehouse || !selectedPart || !quantity || quantity <= 0}
            />
        </div>
    );

    return (
        <Dialog
            header={th('dealerStockAdd.dialog.title')}
            visible={visible}
            style={{ width: '50vw', maxWidth: '600px' }}
            footer={footer}
            onHide={onHide}
            maximizable
        >
            <div className="grid">
                <div className="col-12">
                    <div className="field">
                        <label htmlFor="dealer" className="block mb-2 font-medium">
                            {th('dealerStockAdd.dialog.dealer')} <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            key={dealerDropdownKey}
                            id="dealer"
                            value={selectedDealer}
                            options={dealerOptions}
                            onChange={(e) => setSelectedDealer(e.value)}
                            onFilter={handleDealerFilter}
                            onHide={handleDealerHide}
                            optionLabel="displayValue"
                            filter
                            showClear
                            placeholder={th('dealerStockAdd.dialog.dealerPlaceholder')}
                            className="w-full"
                            loading={isLoadingDealers}
                            virtualScrollerOptions={{ itemSize: 38 }}
                            itemTemplate={(option) => (
                                <div>
                                    <span style={{ fontWeight: 'bold' }}>{option.id}</span> - {option.name}
                                </div>
                            )}
                        />
                    </div>
                </div>

                <div className="col-12">
                    <div className="field">
                        <label htmlFor="warehouse" className="block mb-2 font-medium">
                            {th('dealerStockAdd.dialog.warehouse')} <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            id="warehouse"
                            value={selectedWarehouse}
                            options={warehouseOptions}
                            onChange={(e) => setSelectedWarehouse(e.value)}
                            optionLabel="displayValue"
                            placeholder={selectedDealer ? th('dealerStockAdd.dialog.warehousePlaceholder') : th('dealerStockAdd.dialog.selectDealerFirst')}
                            className="w-full"
                            disabled={!selectedDealer}
                            loading={isLoadingWarehouses}
                            showClear
                        />
                    </div>
                </div>

                <div className="col-12">
                    <div className="field">
                        <label htmlFor="part" className="block mb-2 font-medium">
                            {th('dealerStockAdd.dialog.part')} <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            key={partDropdownKey}
                            id="part"
                            value={selectedPart}
                            options={partOptions}
                            onChange={(e) => setSelectedPart(e.value)}
                            onFilter={handlePartFilter}
                            onHide={handlePartHide}
                            optionLabel="displayValue"
                            filter
                            showClear
                            placeholder={selectedWarehouse ? th('dealerStockAdd.dialog.partPlaceholder') : th('dealerStockAdd.dialog.selectWarehouseFirst')}
                            className="w-full"
                            disabled={!selectedDealer || !selectedWarehouse}
                            loading={isLoadingParts}
                            virtualScrollerOptions={{ itemSize: 38 }}
                        />
                    </div>
                </div>

                <div className="col-12">
                    <div className="field">
                        <div className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="quantity" className="font-medium">
                                {th('dealerStockAdd.dialog.quantity')} <span className="text-red-500">*</span>
                            </label>
                            {selectedPart && (
                                <span
                                    className="inline-flex align-items-center gap-1 px-2 py-1 border-round"
                                    style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.85rem' }}
                                >
                                    <i className="pi pi-box" style={{ fontSize: '0.8rem' }} />
                                    {th('dealerStockAdd.dialog.currentStock')}: {selectedPart.stockQuantity ?? 0}
                                </span>
                            )}
                        </div>
                        <InputNumber
                            id="quantity"
                            value={quantity}
                            onValueChange={(e) => setQuantity(e.value)}
                            min={0}
                            showButtons
                            className="w-full"
                            placeholder={th('dealerStockAdd.dialog.quantityPlaceholder')}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AddStockDialog;
